import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  InputAdornment,
  FormHelperText,
} from '@mui/material';

interface PaymentAmountSelectorProps {
  balanceOwing: number;
  onChange: (amount: number, isCustom: boolean) => void;
  defaultToFull?: boolean;
}

/**
 * PaymentAmountSelector Component
 * 
 * Allows users to select between:
 * - Full payment (entire balance owing)
 * - Custom payment amount (partial payment)
 */
const PaymentAmountSelector: React.FC<PaymentAmountSelectorProps> = ({
  balanceOwing,
  onChange,
  defaultToFull = true,
}) => {
  const [paymentType, setPaymentType] = useState<'full' | 'custom'>(defaultToFull ? 'full' : 'custom');
  const [customAmount, setCustomAmount] = useState<string>(balanceOwing.toFixed(2));
  const [error, setError] = useState<string>('');

  // Initialize with balance owing and notify parent when it changes
  useEffect(() => {
    setCustomAmount(balanceOwing.toFixed(2));
    
    // If in full payment mode, notify parent of the new balance
    if (paymentType === 'full') {
      onChange(balanceOwing, false);
    }
  }, [balanceOwing, paymentType, onChange]);

  const handlePaymentTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newType = event.target.value as 'full' | 'custom';
    setPaymentType(newType);
    setError('');

    if (newType === 'full') {
      onChange(balanceOwing, false);
    } else {
      const amount = parseFloat(customAmount) || 0;
      validateAndNotify(amount);
    }
  };

  const handleCustomAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;

    // Only allow numbers and decimal point
    if (!/^\d*\.?\d{0,2}$/.test(value) && value !== '') {
      return;
    }

    setCustomAmount(value);
    setError('');

    const amount = parseFloat(value) || 0;
    validateAndNotify(amount);
  };

  const validateAndNotify = (amount: number) => {
    // Validation
    if (amount < 1.00) {
      setError('Minimum payment amount is $1.00');
      onChange(0, true);
      return;
    }

    if (amount > balanceOwing) {
      setError(`Amount cannot exceed balance owing ($${balanceOwing.toFixed(2)})`);
      onChange(0, true);
      return;
    }

    setError('');
    onChange(amount, true);
  };

  const isValidCustomAmount = (): boolean => {
    const amount = parseFloat(customAmount) || 0;
    return amount >= 1.00 && amount <= balanceOwing;
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Payment Amount
      </Typography>

      <RadioGroup value={paymentType} onChange={handlePaymentTypeChange}>
        <FormControlLabel
          value="full"
          control={<Radio />}
          label={
            <Box>
              <Typography variant="body1">
                Pay Full Balance
              </Typography>
              <Typography variant="body2" color="text.secondary">
                ${balanceOwing.toFixed(2)} - Complete payment for this order
              </Typography>
            </Box>
          }
        />
        <FormControlLabel
          value="custom"
          control={<Radio />}
          label={
            <Box>
              <Typography variant="body1">
                Pay Custom Amount
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Make a partial payment (minimum $1.00)
              </Typography>
            </Box>
          }
        />
      </RadioGroup>

      {paymentType === 'custom' && (
        <Box sx={{ mt: 2 }}>
          <TextField
            type="text"
            label="Custom Amount"
            value={customAmount}
            onChange={handleCustomAmountChange}
            variant="standard"
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
            error={!!error}
            helperText={
              error ? error : `Enter amount between $1.00 and $${balanceOwing.toFixed(2)}`
            }
            fullWidth
            autoFocus={paymentType === 'custom'}
            placeholder={balanceOwing.toFixed(2)}
            sx={{ maxWidth: 300 }}
          />
          
          {paymentType === 'custom' && customAmount && isValidCustomAmount() && parseFloat(customAmount) < balanceOwing && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
              <Typography variant="body2" color="info.dark">
                <strong>Partial Payment:</strong> ${ parseFloat(customAmount).toFixed(2)}
                <br />
                <strong>Remaining Balance:</strong> ${(balanceOwing - parseFloat(customAmount)).toFixed(2)}
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {balanceOwing < 1.00 && (
        <Box sx={{ mt: 2, p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
          <Typography variant="body2" color="success.dark">
            No payment required - your order is already paid in full!
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default PaymentAmountSelector;
